import React, { useEffect } from "react"
import { ThemeProvider } from "styled-components"

import componentThemeExtension from "utils/componentThemeExtension"
import makeSecureImageUrl from "utils/makeSecureImageUrl"

import IconSprite from "icons"
import InstagramIcon from "icons/instagram-icon"
import SEO from "components/seo"
import ContentfulNavigation from "components/page-components/navigation"
import ImagePreload from "components/image-preload"
import ArticleCard from "components/card/article"
import {
  Container,
  Bio,
  Info,
  Title,
  Description,
  SocialMediaTitle,
  SocialMediaIcons,
  SocialMediaLink,
  LogoWrapper,
  Logo,
  Bottom,
  StyledGrid,
  ConnectWrapper,
  ConnectTitle,
  ConnectFeed,
} from "./styles"

const Partner = ({ partner }) => {
  const { name, description, logo, instagram, facebookPage, pinterest, twitter, articles, url } =
    partner

  useEffect(() => {
    if (twitter) {
      const script = document.createElement("script")
      script.src = "https://platform.twitter.com/widgets.js"
      const element = document.querySelector("#twitter-feed")
      element.appendChild(script)

      return () => element.removeChild(script)
    }
  }, [twitter])

  useEffect(() => {
    if (facebookPage) {
      const script = document.createElement("script")
      script.src = "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v13.0"
      script.async = true
      script.defer = true
      script.crossOrigin = "anonymous"
      script.nonce = "HUAcgsEF"
      const element = document.querySelector("#fb-page")
      element.appendChild(script)

      return () => element.removeChild(script)
    }
  }, [facebookPage])

  const renderSocialMediaLink = (link, Icon, title, color) => (
    <SocialMediaLink href={link} target="_blank" rel="noreferrer">
      <IconSprite id={Icon} height={20} width={20} title={title} color={color} />
    </SocialMediaLink>
  )

  const images = [
    {
      screenSize: 1024,
      transforms: `e_trim/c_fit,w_360,h_360,q_90,f_auto,fl_lossy`,
    },
    {
      screenSize: 768,
      transforms: "e_trim/c_fit,w_220,h_220,q_90,f_auto,fl_lossy",
    },
    {
      transforms: "e_trim/c_fit,w_200,h_200,q_90,f_auto,fl_lossy",
    },
  ]

  return (
    <>
      <ContentfulNavigation />
      <SEO
        title={name}
        description={description}
        robots="noindex"
        {...(logo && {
          image: makeSecureImageUrl(logo, images[2].transforms),
        })}
        canonical={`${process.env.GATSBY_LOCAL_SITE}/partner/${url}`}
      />
      <ThemeProvider theme={componentThemeExtension(null)}>
        <Container>
          <Bio>
            {logo && (
              <LogoWrapper>
                <ImagePreload baseUrl={logo} images={images} />
                <Logo baseUrl={makeSecureImageUrl(logo, "")} images={images} alt={name} />
              </LogoWrapper>
            )}
            <Info>
              <Title>{name}</Title>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
              {(instagram || facebookPage || twitter || pinterest) && (
                <SocialMediaTitle>Connect With {name}</SocialMediaTitle>
              )}
              <SocialMediaIcons>
                {instagram && (
                  <SocialMediaLink
                    href={`https://www.instagram.com/${instagram}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <InstagramIcon title="Instagram" />
                  </SocialMediaLink>
                )}
                {facebookPage &&
                  renderSocialMediaLink(facebookPage, "facebook-icon", "Facebook", "#3b5998")}
                {twitter &&
                  renderSocialMediaLink(
                    `https://twitter.com/${twitter}`,
                    "twitter-icon",
                    "Twitter",
                    "#4099ff",
                  )}
                {pinterest &&
                  renderSocialMediaLink(
                    `https://www.pinterest.com/${pinterest}`,
                    "pinterest-icon",
                    "Pinterest",
                    "#bd081c",
                  )}
              </SocialMediaIcons>
            </Info>
          </Bio>

          <Bottom>
            {articles && articles.length > 0 && (
              <StyledGrid
                title={`Brought To You By ${name}`}
                numColumns={{ default: 3, desktopL: 2, desktop: 1, tablet: 1, mobile: 1 }}
                rowMargin="2.2rem"
                colMargin="2.6rem"
              >
                {articles.map((a) => (
                  <ArticleCard
                    key={a.id}
                    headline={a.titles.site || a.titles.body}
                    category={a.category}
                    link={`/articles/${a.url}`}
                    localLink={false}
                    imageUrl={`//res.mindbodygreen.com/${JSON.parse(a.previewImage).path}`}
                    lazyLoadImage={false}
                    isGrid
                    {...(a.partner && {
                      author: a.partner.name,
                      partnerUrl: `/partner/${a.partner.url}`,
                      partnerLogoUrl: a.partner.previewLogo || a.partner.logo,
                    })}
                    {...(a.authors &&
                      a.authors.length && {
                        author: a.authors[0].name,
                        authorUrl: `/wc/${a.authors[0].url}`,
                      })}
                  />
                ))}
              </StyledGrid>
            )}
            {(twitter || facebookPage) && (
              <ConnectWrapper>
                <ConnectTitle>Connect</ConnectTitle>
                {twitter && (
                  <ConnectFeed id="twitter-feed">
                    <a
                      className="twitter-timeline"
                      data-height="600"
                      data-theme="light"
                      href={`https://twitter.com/${twitter}?ref_src=twsrc%5Etfw`}
                    >
                      Tweets by {twitter}
                    </a>
                  </ConnectFeed>
                )}
                {facebookPage && (
                  <ConnectFeed id="fb-page">
                    <div
                      className="fb-page"
                      data-href={`${facebookPage}`}
                      data-width="600"
                      data-tabs="timeline,events,messages"
                      data-hide-cover="false"
                    >
                      <div className="fb-xfbml-parse-ignore">
                        <blockquote cite={`${facebookPage}`}>
                          <a href={`${facebookPage}`} target="_blank" rel="noreferrer">
                            {name}
                          </a>
                        </blockquote>
                      </div>
                    </div>
                  </ConnectFeed>
                )}
              </ConnectWrapper>
            )}
          </Bottom>
        </Container>
      </ThemeProvider>
    </>
  )
}

export default Partner
